import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  mapToShortDate,
  mapToShortDateTime,
  parseDateTime,
} from '../../../utils/time';
import { BaseChangeHistoryItemDetails } from './Types';
import { GetUserNameById } from './UserNamesProvider';

const testIds = {
  row: (testId: string) => `ChangeHistory::SectionHeader::${testId}`,
  title: 'ChangeHistory::SectionHeader::Title',
  validityStart: 'ChangeHistory::SectionHeader::ValidityStart',
  validityEnd: 'ChangeHistory::SectionHeader::ValidityEnd',
  validityCombined: 'ChangeHistory::SectionHeader::ValidityCombined',
  changedBy: 'ChangeHistory::SectionHeader::ChangedBy',
  changed: 'ChangeHistory::SectionHeader::Changed',
  changedCombined: 'ChangeHistory::SectionHeader::ChangedCombined',
};

type ChangeHistoryItemSectionHeaderRowProps = {
  readonly className?: string;
  readonly getUserNameById: GetUserNameById;
  readonly historyItem: BaseChangeHistoryItemDetails;
  readonly sectionTitle: ReactNode;
  readonly testId: string;
};

export const ChangeHistoryItemSectionHeaderRow: FC<
  ChangeHistoryItemSectionHeaderRowProps
> = ({
  className,
  getUserNameById,
  historyItem,
  sectionTitle,
  testId,
}: ChangeHistoryItemSectionHeaderRowProps) => {
  const changed = parseDateTime(historyItem.changed);

  return (
    <tr
      className={twMerge('bg-hsl-neutral-blue align-top', className)}
      data-testid={testIds.row(testId)}
      data-timestamp={changed
        ?.toInstant()
        .toString({ fractionalSecondDigits: 3 })}
      data-validity-start={historyItem.validityStart?.toString()}
      data-validity-end={historyItem.validityEnd?.toString()}
    >
      {/* Title spans all the way through the name and value fields */}
      <td
        className="py-3 pr-2 pl-2 xl:pr-5"
        colSpan={3}
        data-testid={testIds.title}
      >
        {sectionTitle}
      </td>

      {/* See the <ChangeHistoryTable> component for more details. */}
      <td
        className="hidden px-2 py-3 text-right lg:table-cell xl:px-5"
        data-testid={testIds.validityStart}
      >
        {mapToShortDate(historyItem.validityStart)}
      </td>
      <td
        className="hidden px-2 py-3 text-right lg:table-cell xl:px-5"
        data-testid={testIds.validityEnd}
      >
        {mapToShortDate(historyItem.validityEnd)}
      </td>
      <td
        className="px-2 py-3 text-right lg:hidden xl:px-5"
        data-testid={testIds.validityCombined}
      >
        <div>{mapToShortDate(historyItem.validityStart)}</div>
        <div>{mapToShortDate(historyItem.validityEnd)}</div>
      </td>

      {/* See the <ChangeHistoryTable> component for more details. */}
      <td
        className="hidden px-2 py-3 text-right lg:table-cell xl:px-5"
        data-testid={testIds.changedBy}
      >
        {getUserNameById(historyItem.changedBy) ?? 'HSL'}
      </td>
      <td
        className="hidden py-3 pr-2 pl-2 text-right text-nowrap lg:table-cell xl:pl-5"
        data-testid={testIds.changed}
      >
        {mapToShortDateTime(changed)}
      </td>
      <td
        className="py-3 pr-2 pl-2 text-right text-nowrap lg:hidden xl:pl-5"
        data-testid={testIds.changedCombined}
      >
        <div>{getUserNameById(historyItem.changedBy) ?? 'HSL'}</div>
        <div>{mapToShortDateTime(changed)}</div>
      </td>
    </tr>
  );
};
